import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { hasCompletedOnboarding, storeAuth } from "../lib/auth";

const LOGIN_ENDPOINT = "/broker/login";

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  jwt: string;
}

export function Login() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const form = new FormData(event.currentTarget);
    const credentials: LoginCredentials = {
      email: String(form.get("email") ?? "").trim(),
      password: String(form.get("password") ?? ""),
    };

    try {
      const { jwt } = await mockBrokerLogin(LOGIN_ENDPOINT, credentials);
      storeAuth(jwt);
      navigate(hasCompletedOnboarding() ? "/properties" : "/onboarding", {
        replace: true,
      });
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Login failed.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-mls-900 p-6">
      <section className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <div className="mb-8 flex items-center gap-3">
          <span className="grid size-11 place-items-center rounded-xl bg-mls-900 text-sm font-bold text-emerald-200">
            M2
          </span>
          <div>
            <p className="font-semibold text-slate-950">MLS2</p>
            <p className="text-xs text-slate-500">Broker workspace</p>
          </div>
        </div>

        <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
          Welcome back
        </h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Sign in to review properties, cooperation signals, and broker inventory.
        </p>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-slate-700">
            Email
            <input
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-mls-600 focus:ring-2 focus:ring-mls-600/20"
              name="email"
              type="email"
              autoComplete="email"
              required
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Password
            <input
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-mls-600 focus:ring-2 focus:ring-mls-600/20"
              name="password"
              type="password"
              autoComplete="current-password"
              minLength={8}
              required
            />
          </label>

          {error ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {error}
            </p>
          ) : null}

          <button
            className="w-full rounded-lg bg-mls-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-mls-600 disabled:cursor-not-allowed disabled:opacity-60"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-slate-500">
          Local mock: credentials never leave this browser.
        </p>
      </section>
    </main>
  );
}

async function mockBrokerLogin(
  endpoint: string,
  credentials: LoginCredentials,
): Promise<LoginResponse> {
  await new Promise((resolve) => window.setTimeout(resolve, 350));

  if (endpoint !== LOGIN_ENDPOINT) {
    throw new Error("Unknown login endpoint.");
  }

  if (!credentials.email || credentials.password.length < 8) {
    throw new Error("Enter a valid email and a password of at least 8 characters.");
  }

  const now = Math.floor(Date.now() / 1000);
  return {
    jwt: [
      encodeJwtPart({ alg: "none", typ: "JWT" }),
      encodeJwtPart({
        sub: "broker-local",
        role: "broker",
        mock: true,
        iat: now,
        exp: now + 60 * 60,
      }),
      "local-mock-signature",
    ].join("."),
  };
}

function encodeJwtPart(value: object): string {
  return btoa(JSON.stringify(value))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}